import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import timeTrackerController, { IEmployeeMonthDay, IEmployeeMonthReport, ITimeTrackingLiveEmployee } from '../../../Api/TimeTracker';
import { useLocalStorageState } from '../utils/useLocalStorageState';

export type EmployeeMonthViewProps = {
  employeeId: string;
  employeeName: string | null;
  onBack: () => void;
};

const formatMs = (ms: number) => {
  if (!Number.isFinite(ms) || ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  if (hours > 0) return `${two(hours)}:${two(minutes)}:${two(seconds)}`;
  return `${two(minutes)}:${two(seconds)}`;
};

const isLate = (arrivalAtIso: string | null) => {
  if (!arrivalAtIso) return false;
  const d = dayjs(arrivalAtIso);
  if (!d.isValid()) return false;
  const limit = d.hour(9).minute(50).second(0).millisecond(0);
  return d.isAfter(limit);
};

const isEarlyLeave = (departureAtIso: string | null) => {
  if (!departureAtIso) return false;
  const d = dayjs(departureAtIso);
  if (!d.isValid()) return false;
  const limit = d.hour(18).minute(0).second(0).millisecond(0);
  return d.isBefore(limit);
};

const EmployeeMonthView: FC<EmployeeMonthViewProps> = ({ employeeId, employeeName, onBack }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const now = dayjs();
  const storageKey = `timeTracker:employeeMonth:${employeeId}`;
  const [monthState, setMonthState] = useLocalStorageState<{ year: number; month: number }>(storageKey, {
    year: now.year(),
    month: now.month() + 1,
  });

  const [report, setReport] = useState<IEmployeeMonthReport | null>(null);
  const [liveToday, setLiveToday] = useState<ITimeTrackingLiveEmployee | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const res = await timeTrackerController.getEmployeeMonthReport(employeeId, monthState.year, monthState.month);
    if (!res.ok || !res.report) {
      setReport(null);
      setError(res.message || 'Nie udało się pobrać raportu');
      setIsLoading(false);
      return;
    }

    setReport(res.report);
    setIsLoading(false);
  }, [employeeId, monthState.year, monthState.month]);

  const fetchTodayStatus = useCallback(async () => {
    const res = await timeTrackerController.getLive();
    if (!res.ok) {
      setLiveToday(null);
      return;
    }

    const found = (res.employees || []).find((e) => e.employeeId === employeeId) || null;
    setLiveToday(found);
  }, [employeeId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    fetchTodayStatus();
  }, [fetchTodayStatus]);

  const monthLabel = useMemo(() => {
    const d = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-01`);
    return d.isValid() ? d.locale('pl').format('MMMM YYYY') : `${monthState.month}.${monthState.year}`;
  }, [monthState.month, monthState.year]);

  const printPdf = useCallback(() => {
    if (!report) {
      setError('Najpierw pobierz raport (Odśwież).');
      return;
    }
    // Tymczasowo wyłączone — zachowujemy kod do późniejszego wdrożenia.
    setError('Drukuj / PDF jest tymczasowo wyłączone (kod jest zachowany w pliku).');

    /*
    if (!report) return;

    const escapeHtml = (value: unknown) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const monthDate = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-01`);
    const monthTitle = monthDate.isValid() ? monthDate.locale('pl').format('MMMM YYYY') : `${monthState.month}.${monthState.year}`;
    const dim = monthDate.isValid() ? monthDate.daysInMonth() : 30;

    const formatHm = (iso: string | null) => {
      if (!iso) return '-';
      const d = dayjs(iso);
      return d.isValid() ? d.format('HH:mm') : '-';
    };

    const rows: Array<{
      date: string;
      dayNumber: number;
      arrival: string;
      departure: string;
      presence: string;
      przerwa: string;
      papieros: string;
      piec: string;
    }> = [];

    for (let day = 1; day <= dim; day++) {
      const date = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
      if (!date.isValid()) continue;
      if (date.day() === 0) continue; // Sundays

      const key = date.format('YYYY-MM-DD');
      const data = report?.days?.[key];

      rows.push({
        date: key,
        dayNumber: day,
        arrival: formatHm(data?.arrivalAt || null),
        departure: formatHm(data?.departureAt || null),
        presence: formatMs(data?.totalsMs?.wejscie || 0),
        przerwa: formatMs(data?.totalsMs?.przerwa || 0),
        papieros: formatMs(data?.totalsMs?.papieros || 0),
        piec: formatMs(data?.totalsMs?.piec || 0),
      });
    }

    const docTitle = `Raport - ${employeeName || 'Pracownik'} - ${monthTitle}`;

    const html = `<!doctype html>
<html lang="pl">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(docTitle)}</title>
    <style>
      :root { color-scheme: light; }
      body { font-family: Arial, Helvetica, sans-serif; margin: 24px; color: #111; }
      h1 { font-size: 18px; margin: 0 0 6px; }
      .meta { color: #444; font-size: 12px; margin-bottom: 16px; }
      .chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0 16px; }
      .chip { border: 1px solid #bbb; border-radius: 999px; padding: 6px 10px; font-size: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; vertical-align: middle; }
      th { background: #f5f5f5; text-align: left; }
      .right { text-align: right; }
      @media print {
        body { margin: 12mm; }
        .no-print { display: none !important; }
      }
    </style>
  </head>
  <body>
    <div class="no-print" style="margin-bottom: 10px;">
      <button onclick="window.print()" style="padding: 8px 12px;">Drukuj / Zapisz jako PDF</button>
    </div>
    <h1>${escapeHtml(employeeName || 'Pracownik')} — raport miesięczny</h1>
    <div class="meta">Miesiąc: ${escapeHtml(monthTitle)} • Wygenerowano: ${escapeHtml(dayjs().format('YYYY-MM-DD HH:mm'))}</div>

    <div class="chips">
      <div class="chip">Dni obecne: ${escapeHtml(report.stats.daysPresent)}</div>
      <div class="chip">Spóźnienia &gt; 09:50: ${escapeHtml(report.stats.lateCount)}</div>
      <div class="chip">Wyjścia &lt; 18:00: ${escapeHtml(report.stats.earlyLeaveCount)}</div>
      <div class="chip">W pracy (miesiąc): ${escapeHtml(formatMs(report.stats.totalPresenceMs))}</div>
      <div class="chip">Przerwy (miesiąc): ${escapeHtml(formatMs(report.stats.totalBreakMs))}</div>
      <div class="chip">Papierosy (miesiąc): ${escapeHtml(formatMs(report.stats.totalSmokingMs))}</div>
      <div class="chip">Piec (miesiąc): ${escapeHtml(formatMs(report.stats.totalFurnaceMs))}</div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Data</th>
          <th class="right">Dzień</th>
          <th class="right">Wejście</th>
          <th class="right">Wyjście</th>
          <th class="right">W pracy</th>
          <th class="right">Przerwa</th>
          <th class="right">Papieros</th>
          <th class="right">Piec</th>
        </tr>
      </thead>
      <tbody>
        ${rows
        .map(
          (r) => `
          <tr>
            <td>${escapeHtml(r.date)}</td>
            <td class="right">${escapeHtml(r.dayNumber)}</td>
            <td class="right">${escapeHtml(r.arrival)}</td>
            <td class="right">${escapeHtml(r.departure)}</td>
            <td class="right">${escapeHtml(r.presence)}</td>
            <td class="right">${escapeHtml(r.przerwa)}</td>
            <td class="right">${escapeHtml(r.papieros)}</td>
            <td class="right">${escapeHtml(r.piec)}</td>
          </tr>`
        )
        .join('')}
      </tbody>
    </table>
  </body>
</html>`;

    // Druk bez popupów: ukryty iframe
    try {
      const existing = document.getElementById('tt-print-frame');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      const iframe = document.createElement('iframe');
      iframe.id = 'tt-print-frame';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) throw new Error('Brak dostępu do iframe');

      doc.open();
      doc.write(html);
      doc.close();

      // Daj przeglądarce chwilę na zaciągnięcie stylów/layout
      window.setTimeout(() => {
        try {
          win.focus();
          win.print();
        } finally {
          window.setTimeout(() => {
            try {
              iframe.parentNode?.removeChild(iframe);
            } catch (e) {
              // ignore
            }
          }, 500);
        }
      }, 150);
      return;
    } catch (e) {
      // fallback poniżej
    }

    // Fallback: nowe okno (może być zablokowane przez popup-blocker)
    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) {
      setError('Nie udało się otworzyć wydruku. Włącz pop-upy dla tej strony lub użyj trybu bez pop-upów (iframe).');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    window.setTimeout(() => {
      try {
        w.print();
      } catch (e) {
        // ignore
      }
    }, 150);
    */
  }, [report, setError]);

  const daysInMonth = useMemo(() => {
    const d = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-01`);
    return d.isValid() ? d.daysInMonth() : 30;
  }, [monthState.month, monthState.year]);

  const cells = useMemo(() => {
    const first = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-01`);
    if (!first.isValid()) return [] as Array<{ kind: 'blank' } | { kind: 'day'; key: string; dayNumber: number; data?: IEmployeeMonthDay }>;

    // znajdź pierwszy dzień nie-niedziela
    let firstNonSunday = first;
    while (firstNonSunday.day() === 0 && firstNonSunday.date() <= daysInMonth) {
      firstNonSunday = firstNonSunday.add(1, 'day');
    }

    const leadingBlanks = Math.max(0, (firstNonSunday.day() || 0) - 1); // Mon=1 -> 0 blanks

    const out: Array<{ kind: 'blank' } | { kind: 'day'; key: string; dayNumber: number; data?: IEmployeeMonthDay }> = [];
    for (let i = 0; i < leadingBlanks; i++) out.push({ kind: 'blank' });

    for (let day = 1; day <= daysInMonth; day++) {
      const date = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
      if (!date.isValid()) continue;
      if (date.day() === 0) continue; // ignore Sundays

      const key = date.format('YYYY-MM-DD');
      const data = report?.days?.[key];
      out.push({ kind: 'day', key, dayNumber: day, data });
    }

    // trailing blanks
    while (out.length % 6 !== 0) out.push({ kind: 'blank' });

    return out;
  }, [daysInMonth, monthState.month, monthState.year, report?.days]);

  const todaySummary = useMemo(() => {
    const present = !!liveToday?.present;
    const startedAt = liveToday?.presence?.startedAt ? dayjs(liveToday.presence.startedAt) : null;
    const startedAtLabel = startedAt && startedAt.isValid() ? startedAt.format('HH:mm') : '';

    const presenceMs = liveToday?.presence?.durationMs || 0;
    const przerwa = liveToday?.activities?.przerwa;
    const papieros = liveToday?.activities?.papieros;
    const piec = liveToday?.activities?.piec;

    const totalPrzerwyMs = (przerwa?.totalMs || 0) + (przerwa?.open ? przerwa.currentMs || 0 : 0);
    const totalPapierosyMs = (papieros?.totalMs || 0) + (papieros?.open ? papieros.currentMs || 0 : 0);
    const totalPiecMs = (piec?.totalMs || 0) + (piec?.open ? piec.currentMs || 0 : 0);

    return {
      present,
      startedAtLabel,
      presenceMs,
      przerwa: { open: !!przerwa?.open, ms: totalPrzerwyMs },
      papieros: { open: !!papieros?.open, ms: totalPapierosyMs },
      piec: { open: !!piec?.open, ms: totalPiecMs },
    };
  }, [liveToday]);

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" gap={1} alignItems="center">
            <Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined">
              Wróć
            </Button>
            <Typography variant="h6" fontWeight={900}>
              {employeeName || 'Pracownik'}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Status dzisiaj + raport miesięczny • {monthLabel}
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            onClick={() => {
              const d = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-01`).subtract(1, 'month');
              setMonthState({ year: d.year(), month: d.month() + 1 });
            }}
            fullWidth={!isDesktop}
          >
            Poprzedni
          </Button>
          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            onClick={() => {
              const d = dayjs(`${monthState.year}-${String(monthState.month).padStart(2, '0')}-01`).add(1, 'month');
              setMonthState({ year: d.year(), month: d.month() + 1 });
            }}
            fullWidth={!isDesktop}
          >
            Następny
          </Button>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
            onClick={async () => {
              await Promise.all([fetchReport(), fetchTodayStatus()]);
            }}
            disabled={isLoading}
            fullWidth={!isDesktop}
          >
            Odśwież
          </Button>

          {/* <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={printPdf}
            fullWidth={!isDesktop}
          >
            Drukuj / PDF
          </Button> */}
        </Stack>
      </Stack>

      {error ? (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : null}

      <Divider sx={{ my: 2 }} />

      {/* Status dzisiaj (kafelki) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.25,
          mb: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1.75,
            borderRadius: 3,
            borderColor: (theme) => (todaySummary.present ? theme.palette.success.light : theme.palette.divider),
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Obecność dzisiaj
              </Typography>
              <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.15 }}>
                {todaySummary.present ? 'OBECNY' : 'NIEOBECNY'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {todaySummary.present
                  ? `${todaySummary.startedAtLabel ? `od ${todaySummary.startedAtLabel} • ` : ''}${formatMs(todaySummary.presenceMs)}`
                  : 'Brak wejścia'}
              </Typography>
            </Box>
            <Chip
              color={todaySummary.present ? 'success' : 'default'}
              variant={todaySummary.present ? 'filled' : 'outlined'}
              label={todaySummary.present ? 'W pracy' : 'Poza pracą'}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Przerwa (dzisiaj)
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="h6" fontWeight={900}>
              {formatMs(todaySummary.przerwa.ms)}
            </Typography>
            <Chip
              color={todaySummary.przerwa.open ? 'warning' : 'default'}
              variant={todaySummary.przerwa.open ? 'filled' : 'outlined'}
              label={todaySummary.przerwa.open ? 'Trwa' : 'Zamknięta'}
              size="small"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Suma przerw tego dnia
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Papieros (dzisiaj)
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="h6" fontWeight={900}>
              {formatMs(todaySummary.papieros.ms)}
            </Typography>
            <Chip
              color={todaySummary.papieros.open ? 'warning' : 'default'}
              variant={todaySummary.papieros.open ? 'filled' : 'outlined'}
              label={todaySummary.papieros.open ? 'Trwa' : 'Zamknięta'}
              size="small"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Suma papierosów tego dnia
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Piec (dzisiaj)
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
            <Typography variant="h6" fontWeight={900}>
              {formatMs(todaySummary.piec.ms)}
            </Typography>
            <Chip
              color={todaySummary.piec.open ? 'warning' : 'default'}
              variant={todaySummary.piec.open ? 'filled' : 'outlined'}
              label={todaySummary.piec.open ? 'Trwa' : 'Zamknięta'}
              size="small"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Suma pracy przy piecu
          </Typography>
        </Paper>
      </Box>

      {report ? (
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ mb: 2 }}>
          <Chip label={`Dni obecne: ${report.stats.daysPresent}`} />
          <Chip
            color={report.stats.lateCount ? 'error' : 'default'}
            variant={report.stats.lateCount ? 'filled' : 'outlined'}
            label={`Spóźnienia > 09:50: ${report.stats.lateCount}`}
          />
          <Chip
            color={report.stats.earlyLeaveCount ? 'warning' : 'default'}
            variant={report.stats.earlyLeaveCount ? 'filled' : 'outlined'}
            label={`Wyjścia < 18:00: ${report.stats.earlyLeaveCount}`}
          />
          <Chip label={`W pracy (miesiąc): ${formatMs(report.stats.totalPresenceMs)}`} />
          <Chip label={`Przerwy (miesiąc): ${formatMs(report.stats.totalBreakMs)}`} />
          <Chip label={`Papierosy (miesiąc): ${formatMs(report.stats.totalSmokingMs)}`} />
          <Chip label={`Piec (miesiąc): ${formatMs(report.stats.totalFurnaceMs)}`} />
        </Stack>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
          gap: 1,
        }}
      >
        {cells.map((c, idx) => {
          if (c.kind === 'blank') {
            return <Box key={`b-${idx}`} sx={{ minHeight: 96 }} />;
          }

          const arrival = c.data?.arrivalAt ? dayjs(c.data.arrivalAt).format('HH:mm') : null;
          const departure = c.data?.departureAt ? dayjs(c.data.departureAt).format('HH:mm') : null;
          const late = isLate(c.data?.arrivalAt || null);
          const early = isEarlyLeave(c.data?.departureAt || null);

          const presenceChips = (() => {
            const toHm = (iso: string | null | undefined) => {
              if (!iso) return null;
              const d = dayjs(iso);
              return d.isValid() ? d.format('HH:mm') : null;
            };

            const raw = (c.data?.presenceEvents || [])
              .map((e) => ({ at: typeof e?.at === 'string' ? e.at : '', actionPhase: e?.actionPhase }))
              .filter((e) => (e.actionPhase === 'start' || e.actionPhase === 'end') && !!e.at)
              .sort((a, b) => {
                const da = dayjs(a.at);
                const db = dayjs(b.at);
                return da.valueOf() - db.valueOf();
              });

            const ranges: Array<{ key: string; label: string }> = [];

            if (raw.length) {
              let openStart: string | null = null;
              for (let i = 0; i < raw.length; i++) {
                const e = raw[i];
                const t = toHm(e.at);
                if (!t) continue;

                if (e.actionPhase === 'start') {
                  openStart = t;
                  continue;
                }

                // end
                if (openStart) {
                  const label = openStart === t ? `${t}` : `${openStart}–${t}`;
                  ranges.push({ key: `${c.key}-rng-${ranges.length}`, label });
                  openStart = null;
                } else {
                  ranges.push({ key: `${c.key}-end-${ranges.length}`, label: `…–${t}` });
                }
              }
              if (openStart) {
                ranges.push({ key: `${c.key}-start-open`, label: `${openStart}–…` });
              }

              return ranges.length ? ranges : [{ key: `${c.key}-noevt`, label: '-' }];
            }

            // fallback dla starszego backendu
            if (arrival && departure) {
              const label = arrival === departure ? `${arrival}` : `${arrival}–${departure}`;
              return [{ key: `${c.key}-fallback-range`, label }];
            }
            if (arrival) return [{ key: `${c.key}-fallback-arr`, label: `${arrival}–…` }];
            if (departure) return [{ key: `${c.key}-fallback-dep`, label: `…–${departure}` }];
            return [{ key: `${c.key}-fallback-none`, label: '-' }];
          })();

          return (
            <Paper
              key={c.key}
              variant="outlined"
              sx={{
                p: 1.25,
                borderRadius: 3,
                minHeight: 112,
                borderColor: (theme) =>
                  late ? theme.palette.error.main : early ? theme.palette.warning.main : theme.palette.divider,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={900}>{c.dayNumber}</Typography>
                {late ? (
                  <Tooltip title="Spóźnienie (po 09:50)">
                    <Chip size="small" color="error" variant="filled" label="Spóźn." />
                  </Tooltip>
                ) : early ? (
                  <Tooltip title="Wyjście przed 18:00">
                    <Chip size="small" color="warning" variant="filled" label="Wyj." />
                  </Tooltip>
                ) : (
                  <Chip size="small" variant="outlined" label="OK" />
                )}
              </Stack>

              <Stack direction="column" gap={0.4} sx={{ mt: 0.75, alignItems: 'flex-start' }}>
                {presenceChips.map((c) => (
                  <Chip
                    key={c.key}
                    size="small"
                    variant="outlined"
                    label={c.label}
                    sx={{
                      height: 22,
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontWeight: 900,
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                      },
                    }}
                  />
                ))}
              </Stack>

              {c.data ? (
                <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
                  <Chip size="small" variant="outlined" label={`W pracy: ${formatMs(c.data.totalsMs.wejscie)}`} />
                  {c.data.totalsMs.przerwa ? (
                    <Chip size="small" variant="outlined" label={`Przerwa: ${formatMs(c.data.totalsMs.przerwa)}`} />
                  ) : null}
                  {c.data.totalsMs.papieros ? (
                    <Chip size="small" variant="outlined" label={`Papieros: ${formatMs(c.data.totalsMs.papieros)}`} />
                  ) : null}
                  {c.data.totalsMs.piec ? (
                    <Chip size="small" variant="outlined" label={`Piec: ${formatMs(c.data.totalsMs.piec)}`} />
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Brak danych
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

export default EmployeeMonthView;
